import { useState, useEffect, useMemo } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import TextField from '@mui/material/TextField';
import { Typography } from '@mui/material';
import {
  getCalendarioRestricciones,
  getCachedCalendarioRestricciones,
} from '../services/instancias.service.js';
import BurbujasLoader from './UIElements/BurbujasLoader';
import Box from '@mui/material/Box';

const Fecha = ({ mensaje, getFecha, id, fieldFecha, value, esCampusCordoba, esExcepcionParaFechas = false, fechaDesdeActual, fechaHastaAnterior, instanciasExistentes = [], error = false, helperText = "", inputRef }) => {
  const requiresRemoteRestrictions =
    !fieldFecha ||
    fieldFecha === 'fechaCursadaDesde' ||
    fieldFecha === 'fechaCursadaHasta';

  const initialCache = requiresRemoteRestrictions ? getCachedCalendarioRestricciones() : null;

  const [ isLoading, setIsLoading ] = useState(Boolean(requiresRemoteRestrictions && !initialCache));
  const [ restrictions, setRestrictions ] = useState(initialCache);

  useEffect(() => {
    if (!requiresRemoteRestrictions) {
      setIsLoading(false);
      return;
    }

    const cached = getCachedCalendarioRestricciones();
    if (cached) {
      setRestrictions(cached);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    getCalendarioRestricciones()
      .then((data) => {
        if (isMounted) {
          setRestrictions(data);
        }
      })
      .catch((error) => {
        console.error('Error al obtener la matriz de fechas:', error);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [requiresRemoteRestrictions]);

  // Pre-calcular fecha mínima de inicio de cursada según ventanaInicio (desde hoy y desde cohorte anterior)
  const minFechaVentanaHoy = useMemo(() => {
    if (esExcepcionParaFechas) return null;
    const ventana = restrictions?.ventanaInicio ?? 0;
    if (ventana <= 0) return null;
    return dayjs().add(ventana + 1, 'day');
  }, [esExcepcionParaFechas, restrictions?.ventanaInicio]);

  const minFechaCohorteAnterior = useMemo(() => {
    if (esExcepcionParaFechas || !fechaHastaAnterior) return null;
    const ventana = restrictions?.ventanaInicio ?? 0;
    return dayjs(fechaHastaAnterior).add(ventana + 1, 'day');
  }, [esExcepcionParaFechas, fechaHastaAnterior, restrictions?.ventanaInicio]);

  // Pre-calcular rangos bloqueados por cohortes existentes en BD del mismo curso
  const rangosExistentesBloqueados = useMemo(() => {
    if (!Array.isArray(instanciasExistentes) || instanciasExistentes.length === 0) {
      return [];
    }
    const ventana = restrictions?.ventanaInicio ?? 0;
    return instanciasExistentes
      .filter((inst) => inst.estado_instancia !== 'CANC' && inst.fecha_inicio_curso && inst.fecha_fin_curso)
      .map((inst) => {
        const dbInicio = dayjs(inst.fecha_inicio_curso);
        const dbFin = dayjs(inst.fecha_fin_curso);
        return {
          fechaInicioStr: inst.fecha_inicio_curso,
          dbInicio,
          dbFin,
          inicioConVentana: dbInicio.subtract(ventana, 'day'),
          finConVentana: dbFin.add(ventana, 'day'),
        };
      });
  }, [instanciasExistentes, restrictions?.ventanaInicio]);

  // Pre-calcular límites de fecha de fin de cursada (Campus Córdoba 90 días y no solapamiento con cohorte futura en BD)
  const limitesFinCursada = useMemo(() => {
    if (!fechaDesdeActual) return null;

    const startDate = dayjs(fechaDesdeActual);
    if (esExcepcionParaFechas) {
      return { startDate, maxAllowedDate: null };
    }

    let maxAllowedDate = esCampusCordoba ? startDate.add(90, 'day') : null;

    for (const rango of rangosExistentesBloqueados) {
      if (rango.dbInicio.isAfter(startDate, 'day')) {
        const maxAntesDeExistente = rango.inicioConVentana.subtract(1, 'day');
        if (!maxAllowedDate || maxAntesDeExistente.isBefore(maxAllowedDate, 'day')) {
          maxAllowedDate = maxAntesDeExistente;
        }
      }
    }

    return { startDate, maxAllowedDate };
  }, [esExcepcionParaFechas, esCampusCordoba, fechaDesdeActual, rangosExistentesBloqueados]);

  const handleDateChange = (newDate) => {
    const formattedDate = newDate ? dayjs(newDate).format('YYYY-MM-DD') : null;
    getFecha(formattedDate, id, fieldFecha);
  };

  const shouldDisableDate = (date) => {
    const today = dayjs();
    const isWeekend = date.day() === 0 || date.day() === 6;
    const isBeforeToday = date.isBefore(today, 'day');

    if (isWeekend || isBeforeToday) {
      return true;
    }

    if (fieldFecha === "fechaCursadaDesde") {
      const stringFecha = date.format('YYYY-MM-DD');

      if (restrictions?.feriadosSet?.has(stringFecha)) return true;

      // Siempre bloquear duplicado exacto de fecha_inicio_curso para el mismo curso
      for (const rango of rangosExistentesBloqueados) {
        if (rango.fechaInicioStr === stringFecha) {
          return true;
        }
      }

      if (!esExcepcionParaFechas) {
        // Debe transcurrir la cantidad de días de ventanaInicio desde hoy
        if (minFechaVentanaHoy && date.isBefore(minFechaVentanaHoy, 'day')) {
          return true;
        }

        // Debe transcurrir la cantidad de días de ventanaInicio desde la fecha de fin de la cohorte anterior
        if (minFechaCohorteAnterior && date.isBefore(minFechaCohorteAnterior, 'day')) {
          return true;
        }

        // No puede caer dentro del rango de una cohorte existente ni dentro de su ventanaInicio
        for (const rango of rangosExistentesBloqueados) {
          if (
            !date.isBefore(rango.inicioConVentana, 'day') &&
            !date.isAfter(rango.finConVentana, 'day')
          ) {
            return true;
          }
        }

        if (restrictions?.fechasInvalidasSet?.has(stringFecha)) return true;
        if (restrictions?.fechasInhabilitadasSet?.has(stringFecha)) return true;
      }
    }

    if (fieldFecha === "fechaCursadaHasta") {
      const fechaAValidar = date.format('YYYY-MM-DD');

      if (restrictions?.feriadosSet?.has(fechaAValidar)) return true;

      if (fechaDesdeActual) {
        const startDate = dayjs(fechaDesdeActual);
        if (date.isBefore(startDate, 'day') || date.isSame(startDate, 'day')) return true;
      }

      if (!esExcepcionParaFechas) {
        if (restrictions?.fechasInhabilitadasFinSet?.has(fechaAValidar)) return true;

        // No puede estar dentro del rango (incluidos los extremos) de una cohorte existente en BD
        for (const rango of rangosExistentesBloqueados) {
          if (!date.isBefore(rango.dbInicio, 'day') && !date.isAfter(rango.dbFin, 'day')) {
            return true;
          }
        }

        // Validación de límite máximo (90 días Campus Córdoba y margen previo a cohorte existente futura)
        if (limitesFinCursada) {
          if (limitesFinCursada.maxAllowedDate && date.isAfter(limitesFinCursada.maxAllowedDate, 'day')) return true;
          if (date.isBefore(limitesFinCursada.startDate, 'day')) return true;
        }
      }
    }

    return false;
  };

  const minDatePicker = useMemo(() => {
    if (fieldFecha === "fechaCursadaDesde") {
      if (minFechaCohorteAnterior && minFechaVentanaHoy) {
        return minFechaCohorteAnterior.isAfter(minFechaVentanaHoy, 'day')
          ? minFechaCohorteAnterior
          : minFechaVentanaHoy;
      }
      if (minFechaCohorteAnterior) return minFechaCohorteAnterior;
      if (minFechaVentanaHoy) return minFechaVentanaHoy;
    }
    if (fieldFecha === "fechaCursadaHasta" && fechaDesdeActual) {
      return dayjs(fechaDesdeActual).add(1, 'day');
    }
    return dayjs();
  }, [fieldFecha, minFechaCohorteAnterior, minFechaVentanaHoy, fechaDesdeActual]);

  const maxDatePicker = useMemo(() => {
    if (fieldFecha === "fechaCursadaHasta" && limitesFinCursada?.maxAllowedDate) {
      return limitesFinCursada.maxAllowedDate;
    }
    return undefined;
  }, [fieldFecha, limitesFinCursada]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      {/* Contenedor principal para posicionar el spinner encima */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <div className='fecha-container'>
          <Typography
            variant="body1"
            className='fecha-label'
            sx={{ color: error ? 'error.main' : 'inherit', fontWeight: error ? 600 : 'inherit' }}
          >
            {mensaje}
          </Typography>
          <DatePicker
            sx={{
              width: '100%',
              height: '100%',
              ...(error && {
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d32f2f',
                  borderWidth: '2px',
                },
                '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d32f2f',
                  boxShadow: '0 0 0 3px rgba(211, 47, 47, 0.18)',
                },
              }),
            }}
            value={value ? dayjs(value) : null}
            onChange={handleDateChange}
            minDate={minDatePicker}
            maxDate={maxDatePicker}
            slots={{
              textField: (params) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  error={Boolean(error)}
                  helperText={helperText || params?.inputProps?.placeholder}
                  sx={{
                    '& .MuiFormHelperText-root.Mui-error': {
                      fontWeight: 500,
                      fontSize: '0.78rem',
                      marginLeft: '4px',
                    },
                  }}
                />
              ),
            }}
            format="DD/MM/YYYY"
            shouldDisableDate={shouldDisableDate}
            className='fecha-picker'
            disabled={isLoading}
            PopperProps={{
              placement: "bottom-start",
              modifiers: [
                {
                  name: "flip",
                  enabled: true,
                  options: {
                    altBoundary: true,
                    rootBoundary: "viewport",
                    padding: 8,
                  },
                },
                {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                    altAxis: true,
                    altBoundary: true,
                    tether: true,
                    rootBoundary: "viewport",
                    padding: 8,
                  },
                },
              ],
            }}
          />
        </div>

        {/* Spinner de carga superpuesto */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              borderRadius: 'inherit',
            }}
          >
            <BurbujasLoader />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default Fecha;
